import { INTERNAL_PERMISSION_GROUPS } from 'src/constants/internal-access/internalPermissionGroups'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import type { InternalPermissionGroupView } from 'src/interfaces/internalAccess'

export const OTHER_PERMISSIONS_GROUP_LABEL = 'Other'

const KNOWN_PERMISSIONS: string[] = Object.values(InternalPermission)

export const togglePermission = (selected: string[], permission: string): string[] => {
  if (selected.includes(permission)) {
    return selected.filter((selectedPermission) => {
      return selectedPermission !== permission
    })
  }

  return [...selected, permission]
}

/**
 * The group-level checkbox. Only the permissions the editor holds are added or removed — the
 * backend refuses a list containing anything the editor does not hold.
 */
export const toggleGroupPermissions = (
  selected: string[],
  groupPermissions: string[],
  grantable: string[],
): string[] => {
  const togglable = groupPermissions.filter((permission) => {
    return grantable.includes(permission)
  })

  if (togglable.length === 0) {
    return selected
  }

  const isEverySelected = togglable.every((permission) => {
    return selected.includes(permission)
  })

  if (isEverySelected) {
    return selected.filter((permission) => {
      return !togglable.includes(permission)
    })
  }

  const missing = togglable.filter((permission) => {
    return !selected.includes(permission)
  })

  return [...selected, ...missing]
}

/** The catalogue, plus an "Other" group for anything the role holds that this app doesn't know. */
export const buildPermissionGroups = (rolePermissions: string[]): InternalPermissionGroupView[] => {
  const groups = INTERNAL_PERMISSION_GROUPS.map((group) => {
    return { label: group.label, permissions: [...group.permissions] }
  })

  const unknownPermissions = Array.from(
    new Set(
      rolePermissions.filter((permission) => {
        return !KNOWN_PERMISSIONS.includes(permission)
      }),
    ),
  )

  if (unknownPermissions.length === 0) {
    return groups
  }

  return [...groups, { label: OTHER_PERMISSIONS_GROUP_LABEL, permissions: unknownPermissions }]
}

/**
 * The group checkbox's state, measured over the permissions the editor can grant — the same subset
 * `toggleGroupPermissions` acts on. Measured over the whole group, a group holding one un-held
 * permission would read "partial" with everything grantable already ticked, and the next click
 * would clear it. A group with nothing grantable (its checkbox is disabled) shows the whole group.
 */
export const resolveGroupSelectionState = (
  groupPermissions: string[],
  selected: string[],
  grantable: string[],
): { isAllSelected: boolean; isPartiallySelected: boolean } => {
  const grantableInGroup = groupPermissions.filter((permission) => {
    return grantable.includes(permission)
  })
  const measured = grantableInGroup.length > 0 ? grantableInGroup : groupPermissions
  const selectedCount = measured.filter((permission) => {
    return selected.includes(permission)
  }).length
  const isAllSelected = measured.length > 0 && selectedCount === measured.length

  return { isAllSelected, isPartiallySelected: selectedCount > 0 && !isAllSelected }
}
